import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { dateTimeToMonDDYYYYWithTime, convertTo12HourFormat } from '../utils/dateUtils';
import AssignmentViewer from '../components/AssignmentViewer';
import LoadingSpinner from '../components/LoadingSpinner';
import { getValidEnrollment } from '../utils/enrollmentUtils';
import Tooltip from '../components/Tooltip';

const TYPE_CONFIG = [
  { key: 'Field Experience', label: 'Field Experience', required: 45 },
  { key: 'Lab', label: 'Lab', required: 45 },
  { key: 'Concentration', label: 'Concentration', required: 10 },
  { key: 'Capstone', label: 'Capstone', required: 10 },
  { key: 'Theory', label: 'Theory', required: 70 },
];

const MyHours = ({ userData }) => {
  const [registrations, setRegistrations] = useState([]);
  const [projectSubmissions, setProjectSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [systemAssignment, setSystemAssignment] = useState(null);
  const [instructorChoices, setInstructorChoices] = useState([]);
  const [activeSurveyEventId, setActiveSurveyEventId] = useState(null);
  const [activeRegistrationId, setActiveRegistrationId] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyEventDetails, setSurveyEventDetails] = useState(null);
  const [validEnrollment, setValidEnrollment] = useState(null);
  const [selectedType, setSelectedType] = useState('Field Experience'); // Field Experience tab loads first
  const [concentrations, setConcentrations] = useState([]);
  const [hourWaivers, setHourWaivers] = useState([]);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [componentSubmissions, setComponentSubmissions] = useState([]);
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [expandedCourses, setExpandedCourses] = useState({});

  useEffect(() => {
    // Debug logging for enrollment data
    console.log('=== ENROLLMENT DEBUG INFO ===');
    console.log('userData:', userData);
    
    if (userData && userData.accesscards) {
      console.log('All accesscards:', userData.accesscards);
      
      if (userData.default_accesscard_id) {
        console.log('Default accesscard ID:', userData.default_accesscard_id);
        const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
        console.log('Default accesscard:', defaultCard);
        
        if (defaultCard && defaultCard.enrollments) {
          console.log('All enrollments on default card:', defaultCard.enrollments);
        }
      }
    }
    
    const validEnrollment = getValidEnrollment(userData);
    console.log('Valid enrollment returned by getValidEnrollment:', validEnrollment);
    console.log('=== END ENROLLMENT DEBUG INFO ===');
    
    setValidEnrollment(validEnrollment);
  }, [userData]);

  // Fetch all concentrations on mount
  useEffect(() => {
    const fetchConcentrations = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/concentrations', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConcentrations(res.data.concentrations || []);
      } catch (err) {
        setConcentrations([]);
      }
    };
    fetchConcentrations();
  }, []);

  // Helper to get concentration name from id
  const getConcentrationName = (id) => {
    if (!id) return '';
    const found = concentrations.find(
      (c) => c.id === id || c.abbreviation_code === id || c.short_title === id
    );
    return found ? found.title : id;
  };

  const fetchRegistrations = async (enrollmentId) => {
    setError('');
    if (!enrollmentId) {
      setRegistrations([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/feeventregistrations/my-hours?enrollment=${enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(res.data.registrations || []);
    } catch (err) {
      setError('Failed to load your registrations.');
    }
  };

  // Fetch project submissions for valid enrollment
  const fetchProjectSubmissions = async (enrollmentId) => {
    if (!enrollmentId) {
      setProjectSubmissions([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/projectsubmissions/by-enrollment/${enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectSubmissions(res.data.projectSubmissions || []);
    } catch (err) {
      console.log('Error fetching project submissions:', err);
      setProjectSubmissions([]);
    }
  };

  // Fetch hourwaivers for valid enrollment
  const fetchHourWaivers = async (enrollmentId) => {
    if (!enrollmentId) {
      setHourWaivers([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/hourwaivers?enrollment=${enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHourWaivers(res.data.hourwaivers || []);
    } catch (err) {
      setHourWaivers([]);
    }
  };

  // Fetch component submissions for valid enrollment
  const fetchComponentSubmissions = async (enrollmentId) => {
    console.log('=== FETCH COMPONENT SUBMISSIONS DEBUG ===');
    console.log('Enrollment ID passed:', enrollmentId);
    console.log('Type of enrollment ID:', typeof enrollmentId);
    console.log('Valid enrollment object:', validEnrollment);
    
    if (!enrollmentId) {
      console.log('No enrollment ID provided, returning empty array');
      setComponentSubmissions([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const url = `/api/component-submissions/by-enrollment/${enrollmentId}`;
      console.log('Making request to URL:', url);
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('API Response:', res.data);
      const submissions = res.data.componentSubmissions || [];
      const enrollments = res.data.courseEnrollments || [];
      setComponentSubmissions(submissions);
      setCourseEnrollments(enrollments);
      
      // Console log the component submissions results
      console.log('=== COMPONENT SUBMISSIONS FOR ENROLLMENT ===');
      console.log('Enrollment ID:', enrollmentId);
      console.log('Component Submissions:', submissions);
      console.log('Component Submissions Count:', submissions.length);
      
      // Log each submission with details
      submissions.forEach((submission, index) => {
        console.log(`Component Submission ${index + 1}:`, {
          id: submission.id,
          component_title: submission.component_title,
          component_type: submission.component_type,
          component_superparent: submission.component_superparent,
          course_title: submission.course_title,
          course_status: submission.course_status,
          status: submission.status,
          hours_earned: submission.hours_earned,
          component_hourawards: submission.component_hourawards,
          createddate: submission.createddate
        });
      });
      console.log('=== END COMPONENT SUBMISSIONS ===');
      
      // Console log the course enrollments results
      console.log('=== COURSE ENROLLMENTS FOR ENROLLMENT ===');
      console.log('Enrollment ID:', enrollmentId);
      console.log('Course Enrollments:', enrollments);
      console.log('Course Enrollments Count:', enrollments.length);
      
      // Log each course enrollment with details
      enrollments.forEach((enrollment, index) => {
        console.log(`Course Enrollment ${index + 1}:`, {
          id: enrollment.id,
          course_title: enrollment.course_title,
          course_status: enrollment.course_status,
          status: enrollment.status,
          start_date: enrollment.start_date,
          end_date: enrollment.end_date,
          createddate: enrollment.createddate
        });
      });
      console.log('=== END COURSE ENROLLMENTS ===');
      
    } catch (err) {
      console.error('Error fetching component submissions:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setComponentSubmissions([]);
    }
  };

  useEffect(() => {
    if (validEnrollment && validEnrollment.enrollment_id) {
      setLoading(true);
      setError('');
      Promise.all([
        fetchRegistrations(validEnrollment.enrollment_id),
        fetchProjectSubmissions(validEnrollment.enrollment_id),
        fetchHourWaivers(validEnrollment.enrollment_id),
        fetchComponentSubmissions(validEnrollment.enrollment_id)
      ]).then(() => {
        setLoading(false);
      });
    } else if (userData) {
      setRegistrations([]);
      setProjectSubmissions([]);
      setHourWaivers([]);
      setComponentSubmissions([]);
      setCourseEnrollments([]);
      setLoading(false);
    }
  }, [validEnrollment, userData]);

  // Fetch the system assignment (now uses the new endpoint)
  const fetchSystemAssignment = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/assignments/system?title=FE%20Event%20Hour%20Submittal%20Survey', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemAssignment(res.data.assignment || null);
      return res.data.assignment || null;
    } catch (err) {
      setSystemAssignment(null);
      return null;
    }
  };

  // Fetch instructors for the active event when survey is triggered
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!activeSurveyEventId || !systemAssignment) return;
      setSurveyLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Find if the assignment has a question with special choices
        const specialQ = systemAssignment.questions.find(q => typeof q.choices === 'string' && q.choices === '${involvedTrainingPlanInstructors}');
        if (specialQ) {
          // Fetch training plans for the event, including instructor info
          const tpRes = await axios.get(`/api/field-experience-events/trainingplans/by-event/${activeSurveyEventId}`,
            { headers: { Authorization: `Bearer ${token}` } });
          // Get unique instructor names
          const instructorNames = [];
          const seen = new Set();
          for (const tp of tpRes.data.trainingPlans || []) {
            if (tp.instructor && !seen.has(tp.instructor)) {
              seen.add(tp.instructor);
              instructorNames.push({
                name: `${tp.first_name} ${tp.last_name}`,
                account_id: tp.instructor
              });
            }
          }
          setInstructorChoices(instructorNames);
        } else {
          setInstructorChoices([]);
        }
      } catch (err) {
        console.error('Error fetching instructors:', err);
        setInstructorChoices([]);
      } finally {
        setSurveyLoading(false);
      }
    };
    if (activeSurveyEventId && systemAssignment) {
      fetchInstructors();
    }
  }, [activeSurveyEventId, systemAssignment]);

  const handleTakeSurvey = async (registration) => {
    setActiveSurveyEventId(registration.feevent_id);
    setActiveRegistrationId(registration.id);
    // Use event details already available in the registration object
    setSurveyEventDetails({
      title: registration.event_title,
      date: registration.event_date,
      start_time: convertTo12HourFormat(registration.event_start_time),
      end_time: convertTo12HourFormat(registration.event_end_time),
      location: registration.event_location
    });
    // Fetch system assignment before opening modal
    const assignment = await fetchSystemAssignment();
    if (assignment) {
      setShowSurvey(true);
    } else {
      alert('Unable to load survey assignment. Please try again later.');
    }
  };

  const handleSurveySubmit = () => {
    // Survey submitted successfully
    setShowSurvey(false);
    setActiveSurveyEventId(null);
    setActiveRegistrationId(null);
    setSystemAssignment(null);
    setInstructorChoices([]);
    setSurveyEventDetails(null);
    // Reload registrations to show updated status
    if (validEnrollment && validEnrollment.enrollment_id) {
      fetchRegistrations(validEnrollment.enrollment_id);
    } else {
      setRegistrations([]);
      setLoading(false);
    }
  };

  // Helper: Get all items grouped by type
  const getGroupedItems = () => {
    const grouped = {
      'Field Experience': [],
      'Lab': [],
      'Concentration': [],
      'Capstone': [],
      'Theory': [],
    };
    // Add project submissions by their type
    for (const ps of projectSubmissions) {
      const type = ps.type || 'Field Experience';
      if (grouped[type]) grouped[type].push({ ...ps, _source: 'project' });
    }
    // Add feeventregistrations as Field Experience
    for (const reg of registrations) {
      grouped['Field Experience'].push({ ...reg, _source: 'registration' });
    }
    // Add hourwaivers by their type
    for (const hw of hourWaivers) {
      const type = hw.type || 'Field Experience';
      if (grouped[type]) grouped[type].push({ ...hw, _source: 'hourwaiver' });
    }

    // Add coursework by aggregating componentSubmissions into courses and distributing into tabs by hour type
    try {
      if (Array.isArray(courseEnrollments) && Array.isArray(componentSubmissions)) {
        for (const ce of courseEnrollments) {
          const courseId = ce.course;
          const submissionsForCourse = componentSubmissions.filter(s => s.component_superparent === courseId);

          // Aggregate hours earned by type from completed submissions only
          const hoursByType = { 'Field Experience': 0, 'Lab': 0, 'Concentration': 0, 'Capstone': 0, 'Theory': 0 };
          let totalHours = 0;
          const breakdownPairs = [];

          submissionsForCourse.forEach(sub => {
            if (sub.status === 'completed' && sub.hours_earned) {
              try {
                const hours = typeof sub.hours_earned === 'string' ? JSON.parse(sub.hours_earned) : sub.hours_earned;
                if (Array.isArray(hours)) {
                  hours.forEach(h => {
                    const t = h.type;
                    const val = Number(h.hours) || 0;
                    if (hoursByType[t] !== undefined) {
                      hoursByType[t] += val;
                    }
                    totalHours += val;
                  });
                }
              } catch {}
            }
          });

          // Build breakdown text
          for (const [t, val] of Object.entries(hoursByType)) {
            if (val > 0) breakdownPairs.push(`${val} ${t}`);
          }

          // Build base coursework item representing the course
          const baseCourseItem = {
            _source: 'coursework',
            id: ce.id,
            course: courseId,
            course_title: ce.course_title,
            course_status: ce.course_status,
            submissions: submissionsForCourse,
            hoursByType,
            totalHours,
            breakdownText: breakdownPairs.join(', ')
          };

          // Distribute the course into each tab that has non-zero hours for that type
          Object.keys(grouped).forEach(typeKey => {
            if (hoursByType[typeKey] > 0) {
              grouped[typeKey].push({ ...baseCourseItem });
            }
          });
        }
      }
    } catch {}

    return grouped;
  };

  // Helper: Calculate total hours for each type
  const getTypeHours = () => {
    const hours = {
      'Field Experience': 0,
      'Lab': 0,
      'Concentration': 0,
      'Capstone': 0,
      'Theory': 0,
    };
    for (const ps of projectSubmissions) {
      const type = ps.type || 'Field Experience';
      if (ps.status === 'complete' && typeof ps.hoursreceived === 'number') {
        hours[type] += ps.hoursreceived;
      }
    }
    for (const reg of registrations) {
      if (reg.attended === true && reg.status === 'Verified' && typeof reg.hours === 'number') {
        hours['Field Experience'] += reg.hours;
      }
    }
    for (const hw of hourWaivers) {
      const type = hw.type || 'Field Experience';
      if (hw.status === 'Verified' && typeof hw.hours === 'number') {
        hours[type] += hw.hours;
      }
    }
    // Include coursework hours from completed component submissions
    if (Array.isArray(componentSubmissions)) {
      for (const cs of componentSubmissions) {
        if (cs.status === 'completed' && cs.hours_earned) {
          try {
            const earned = typeof cs.hours_earned === 'string' ? JSON.parse(cs.hours_earned) : cs.hours_earned;
            if (Array.isArray(earned)) {
              for (const h of earned) {
                const t = h?.type;
                const v = Number(h?.hours) || 0;
                if (t && hours[t] !== undefined) {
                  hours[t] += v;
                }
              }
            }
          } catch {}
        }
      }
    }
    return hours;
  };

  // Helper: Get count of non-verified items for each type
  const getNonVerifiedCounts = () => {
    const counts = {
      'Field Experience': 0,
      'Lab': 0,
      'Concentration': 0,
      'Capstone': 0,
      'Theory': 0,
    };
    
    // Count non-verified project submissions (status !== "complete")
    for (const ps of projectSubmissions) {
      const type = ps.type || 'Field Experience';
      if (ps.status !== "complete") {
        counts[type]++;
      }
    }
    
    // Count non-verified registrations (status !== "Verified")
    for (const reg of registrations) {
      if (reg.status !== "Verified") {
        counts['Field Experience']++;
      }
    }
    
    // Count non-verified hour waivers (status !== "Verified")
    for (const hw of hourWaivers) {
      const type = hw.type || 'Field Experience';
      if (hw.status !== "Verified") {
        counts[type]++;
      }
    }
    
    return counts;
  };

  const groupedItems = getGroupedItems();
  const typeHours = getTypeHours();
  const nonVerifiedCounts = getNonVerifiedCounts();

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <style>
        {`
          .tab-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
          }
          .item-hover:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
          }
          .button-hover:hover {
            background: #1565c0 !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
          }
          
          /* Subtle button hover effects */
          .subtle-button:hover {
            background: rgba(108, 117, 125, 0.25) !important;
            border-color: rgba(108, 117, 125, 0.5) !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(108, 117, 125, 0.2) !important;
          }
          
          /* Item Badges */
          .item-badge {
            position: absolute;
            top: 4px;
            left: 4px;
            padding: 2px 6px;
            border-radius: 6px;
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            z-index: 1;
          }
          
          .badge-event {
            background: rgba(108, 117, 125, 0.05);
            color: #6c757d;
            border: 1px solid rgba(108, 117, 125, 0.1);
          }
          
          .badge-project {
            background: rgba(108, 117, 125, 0.05);
            color: #6c757d;
            border: 1px solid rgba(108, 117, 125, 0.1);
          }
          
          .badge-external {
            background: rgba(108, 117, 125, 0.05);
            color: #6c757d;
            border: 1px solid rgba(108, 117, 125, 0.1);
          }
          
          /* Modern Action Button */
          .action-trigger-button {
            background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
            color: white;
            border: none;
            border-radius: 12px;
            padding: 14px 28px;
            font-size: 15px;
            font-weight: 600;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(25, 118, 210, 0.3);
            display: flex;
            align-items: center;
            gap: 10px;
            letter-spacing: 0.3px;
            text-transform: none;
          }
          .action-trigger-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(25, 118, 210, 0.4);
            background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
          }
          .action-trigger-button:active {
            transform: translateY(0);
            box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
          }
          
          /* Popup Overlay */
          .popup-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            animation: fadeIn 0.3s ease-out;
          }
          
          .popup {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow: hidden;
            animation: slideUp 0.3s ease-out;
          }
          
          .popup-header {
            background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
            color: white;
            padding: 20px 24px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .popup-header h3 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          }
          
          .popup-close {
            background: rgba(255, 255, 255, 0.2);
            border: none;
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }
          
          .popup-close:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: scale(1.1);
          }
          
          .popup-content {
            padding: 24px;
          }
          
          .popup-option {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 16px;
            border: 2px solid #f1f3f4;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-bottom: 12px;
          }
          
          .popup-option:hover {
            border-color: #1976d2;
            background: #f8f9ff;
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(25, 118, 210, 0.15);
          }
          
          .option-icon {
            font-size: 24px;
            width: 48px;
            height: 48px;
            background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          
          .option-content {
            flex: 1;
          }
          
          .option-title {
            font-size: 16px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 4px;
          }
          
          .option-subtext {
            font-size: 14px;
            color: #6c757d;
            line-height: 1.4;
          }
          
          /* Animations */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 3px 8px rgba(255, 107, 53, 0.4);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 5px 12px rgba(255, 107, 53, 0.6);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 3px 8px rgba(255, 107, 53, 0.4);
            }
          }
          
          /* Modern Card Styling */
          .modern-card {
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e9ecef;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          
          .modern-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
          }
          
          /* Progress Indicators */
          .progress-indicator {
            position: relative;
            overflow: hidden;
            border-radius: 8px;
            background: #f1f3f4;
            height: 6px;
            margin: 8px 0;
          }
          
          .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #1976d2 0%, #1565c0 100%);
            border-radius: 8px;
            transition: width 0.6s ease;
          }
          
          @media (max-width: 768px) {
            .header-container {
              flex-direction: row !important;
              align-items: flex-start !important;
              gap: 12px !important;
            }
            .header-title {
              flex: 1 !important;
            }
            .header-title h1 {
              font-size: 24px !important;
              margin-bottom: 2px !important;
            }
            .header-title p {
              font-size: 13px !important;
            }
            .header-buttons {
              flex-shrink: 0 !important;
            }
            .action-trigger-button {
              padding: 6px 12px !important;
              font-size: 12px !important;
              border-radius: 8px !important;
            }
            .popup {
              width: 95%;
              margin: 20px;
            }
            .popup-content {
              padding: 20px;
            }
          }
        `}
      </style>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Header with title and action buttons */}
          <div className="header-container" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div className="header-title" style={{ 
              margin: 0, 
              flex: '1 1 auto',
              minWidth: '200px'
            }}>
              <h1 style={{ 
                margin: 0, 
                color: '#2c3e50', 
                fontSize: 28, 
                fontWeight: 700,
                marginBottom: '4px',
                background: 'linear-gradient(135deg, #2c3e50 0%, #1976d2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Progress Tracker</h1>
              <p style={{
                margin: 0,
                color: '#6c757d',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.4
              }}>Track your CTE hours from events, projects, coursework, and more</p>
            </div>
            
            {/* Action trigger button */}
            <div className="header-buttons" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}>
              <button
                className="action-trigger-button"
                onClick={() => setShowActionPopup(true)}
              >
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
                Add Hours
              </button>
            </div>
          </div>
          {/* Tab-like boxes */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 0, flexWrap: 'wrap', justifyContent: 'center' }}>
             {TYPE_CONFIG.map(type => (
               <div
                 key={type.key}
                 className="tab-hover"
                 onClick={() => setSelectedType(type.key)}
                 style={{
                   flex: '0 1 120px',
                   minWidth: 100,
                   maxWidth: 140,
                   background: selectedType === type.key ? '#f5faff' : '#f8f9fa',
                   border: selectedType === type.key ? '2px solid #1976d2' : '1px solid #e0e0e0',
                   borderBottom: selectedType === type.key ? 'none' : '1px solid #e0e0e0',
                   borderRadius: '12px 12px 0 0',
                   padding: '8px 6px 6px 6px',
                   cursor: 'pointer',
                   boxShadow: selectedType === type.key ? '0 2px 8px #1976d220' : 'none',
                   transition: 'all 0.2s',
                   textAlign: 'center',
                   position: 'relative',
                   marginBottom: selectedType === type.key ? '-2px' : 0,
                   zIndex: selectedType === type.key ? 2 : 1,
                 }}
               >
                 <div style={{ fontWeight: 700, fontSize: 18, color: selectedType === type.key ? '#1976d2' : '#2c3e50', marginBottom: '4px' }}>{typeHours[type.key] || 0}h</div>
                 <div style={{ fontSize: 13, margin: '2px 0', color: '#555', fontWeight: 500 }}>{type.label}</div>
                 <div style={{ fontSize: 11, color: '#888', marginBottom: '8px' }}>{type.required} required</div>
                 
                 {/* Modern Progress Bar */}
                 <div className="progress-indicator">
                   <div 
                     className="progress-bar" 
                     style={{ 
                       width: `${Math.min(((typeHours[type.key] || 0) / type.required) * 100, 100)}%` 
                     }}
                   />
                 </div>
                {/* Pending items indicator */}
                {nonVerifiedCounts[type.key] > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '-8px',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    minWidth: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    boxShadow: '0 3px 8px rgba(255, 107, 53, 0.4)',
                    zIndex: 1,
                    padding: '0 6px',
                    border: '2px solid white',
                    animation: 'pulse 2s infinite',
                  }}>
                    {nonVerifiedCounts[type.key]} pending
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Items container under the active tab */}
          <div className="modern-card" style={{
            margin: '0 auto 32px auto',
            padding: '28px 24px',
            minHeight: 120,
            maxWidth: 700,
            zIndex: 1,
          }}>
            {showSurvey && systemAssignment && activeRegistrationId ? (
              (() => {
                return (
                  <AssignmentViewer
                    assignment={systemAssignment}
                    mode="survey"
                    interactive={true}
                    instructorChoices={instructorChoices}
                    registrationId={activeRegistrationId}
                    eventDetails={surveyEventDetails}
                    onSurveySubmit={handleSurveySubmit}
                  />
                );
              })()
            ) : (
              <>
                {surveyLoading && <LoadingSpinner />}
                {!surveyLoading && !validEnrollment && userData && (
                  <div style={{
                    color: '#b26a00',
                    background: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: 8,
                    padding: '1.5rem',
                    margin: '2rem 0',
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    textAlign: 'center'
                  }}>
                    Access Restricted — No valid enrollment found for your account.
                  </div>
                )}
                {error && <div style={{ color: 'red' }}>{error}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {groupedItems[selectedType].length === 0 && !surveyLoading && (
                    <div style={{ color: '#6c757d', textAlign: 'center', margin: '2rem 0' }}>No items found for {selectedType}.</div>
                  )}
                {groupedItems[selectedType].map(item => {
                let borderColor = '#ffeaa7'; // default yellow
                if (item._source === 'project' && item.status === 'complete') {
                  borderColor = '#b6e7c9'; // light green
                } else if (item._source === 'registration' && item.attended === true && item.job && item.status === 'Verified') {
                  borderColor = '#b6e7c9'; // light green
                }
                if (item._source === 'project') {
                  // Project Submission display (show concentration name)
                  const typeData = Array.isArray(item.typedata) ? item.typedata.find(t => t.type === item.type) : null;
                  let projectBackgroundColor = item.status === 'complete' ? '#e8f5e8' : '#fffbf0';
                  return (
                    <div key={item.id} className="item-hover" style={{ border: `2px solid ${borderColor}`, borderRadius: 6, padding: '1rem', background: projectBackgroundColor, position: 'relative' }}>
                      <div className="item-badge badge-project">Project</div>
                      <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#2c3e50' }}>{item.projecttitle || 'Untitled Project'}</div>
                      <div style={{ color: '#6c757d', fontSize: '0.95rem' }}>{getConcentrationName(item.concentration) || ''}</div>
                      {item.status === 'started' && typeData && (
                        <div style={{ color: '#1976d2', fontWeight: 500, margin: '6px 0' }}>{typeData.hours} Possible</div>
                      )}
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Status text */}
                        {item.status === 'started' ? (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>In Progress</span>
                        ) : item.status === 'complete' ? (
                          <span style={{ color: '#495057', fontWeight: 600 }}>✅ {item.hoursreceived} Hours Received</span>
                        ) : null}
                        {/* Always show Go to Project button */}
                        <button
                          className={item.status === 'complete' ? 'subtle-button' : ''}
                          style={{ 
                            padding: '6px 16px', 
                            borderRadius: 4, 
                            background: item.status === 'complete' ? 'rgba(108, 117, 125, 0.15)' : (item.status === 'started' ? '#ff6b35' : '#1976d2'), 
                            color: item.status === 'complete' ? '#6c757d' : 'white', 
                            border: item.status === 'complete' ? '1px solid rgba(108, 117, 125, 0.3)' : 'none', 
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13,
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => window.open(`/projects-directory?project_code=${item.projectcode}&command=displayProject`, '_blank')}
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  );
                } else if (item._source === 'registration') {
                  // FE Event Registration display (keep existing)
                  let registrationBackgroundColor = (item.attended === true && item.job && item.status === 'Verified') ? '#e8f5e8' : '#fffbf0';
                  return (
                    <div id={`registration-item-${item.id}`} key={item.id} className="item-hover" style={{ border: `2px solid ${borderColor}`, borderRadius: 6, padding: '1rem', background: registrationBackgroundColor, position: 'relative' }}>
                      <div className="item-badge badge-event">Event</div>
                      <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#2c3e50' }}>{item.event_title || item.feevent_id}</div>
                      <div style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                        {item.event_date ? dateTimeToMonDDYYYYWithTime(item.event_date, false) : 'No date'}
                        {item.event_start_time && item.event_end_time
                          ? ` | ${convertTo12HourFormat(item.event_start_time)} to ${convertTo12HourFormat(item.event_end_time)}`
                          : ''}
                      </div>
                      {item.event_location && (
                        <div style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                          📍 {item.event_location}
                        </div>
                      )}
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Status text */}
                        {item.attended === false && (
                          <span style={{ color: '#dc3545', fontWeight: 600 }}>❌ Absent ❌</span>
                        )}
                        {item.attended === null && (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>❓ Attendance not taken</span>
                        )}
                        {item.attended === true && item.job == null && (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>❓ Missing Job</span>
                        )}
                        {item.attended === true && item.job && item.status === 'Registered' && (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>Submit Survey</span>
                        )}
                        {item.attended === true && item.job && item.status === 'Pending' && (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>⏰ Hours Pending</span>
                        )}
                        {item.attended === true && item.job && item.status === 'Verified' && (
                          <span style={{ color: '#495057', fontWeight: 600 }}>✅ {item.hours} Hours Received</span>
                        )}
                        
                        {/* Action buttons */}
                        {item.attended === true && item.job && item.status === 'Registered' && (
                        <button
                          style={{ 
                            padding: '6px 16px', 
                            borderRadius: 4, 
                            background: '#ff6b35', 
                            color: 'white', 
                            border: 'none', 
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13
                          }}
                          onClick={() => handleTakeSurvey(item)}
                        >
                          Open
                        </button>
                        )}
                        {item.attended === true && item.job && item.status === 'Verified' && item.pdflink && (
                          <a
                            className="subtle-button"
                            href={item.pdflink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '6px 16px',
                              borderRadius: 4,
                              background: 'rgba(108, 117, 125, 0.15)',
                              color: '#6c757d',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13,
                              border: '1px solid rgba(108, 117, 125, 0.3)',
                              transition: 'all 0.2s ease'
                            }}
                            title="Open verification PDF"
                          >
                            Open
                          </a>
                        )}
                      </div>
                    </div>
                  );
                } else if (item._source === 'hourwaiver') {
                  // Hour waiver card
                  let hwBackgroundColor = item.status === 'Verified' ? '#e8f5e8' : '#fffbf0';
                  let hwBorderColor = item.status === 'Verified' ? '#b6e7c9' : '#ffeaa7';
                  return (
                    <div key={item.id} className="item-hover" style={{ border: `2px solid ${hwBorderColor}`, borderRadius: 6, padding: '1rem', background: hwBackgroundColor, marginBottom: 8, position: 'relative' }}>
                      <div className="item-badge badge-external">External Activity</div>
                      <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e50' }}>
                        {item.description ? item.description.substring(0, 30) + (item.description.length > 20 ? '...' : '') : 'External Activity'}
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '0.95rem', marginTop: 2 }}>
                        {item.date ? dateTimeToMonDDYYYYWithTime(item.date, false) : 'No date'}
                        {item.timestart && item.timeend
                          ? ` | ${convertTo12HourFormat(item.timestart)} - ${convertTo12HourFormat(item.timeend)}`
                          : ''}
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '0.95rem', marginTop: 2 }}>
                        {Array.isArray(item.concentrations) && item.concentrations.length > 0
                          ? item.concentrations.map(cid => getConcentrationName(cid)).join(', ')
                          : item.otherconcentration
                            ? `Other: ${item.otherconcentration}`
                            : 'No concentrations'}
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Status text */}
                        {item.status === 'Verified' ? (
                          <span style={{ color: '#495057', fontWeight: 600 }}>✅ {item.hours} Hours Received</span>
                        ) : (
                          <span style={{ color: '#6c757d', fontWeight: 600 }}>{item.status || 'Submitted'}</span>
                        )}
                        <button
                          className={item.status === 'Verified' ? 'subtle-button' : ''}
                          style={{ 
                            padding: '6px 16px', 
                            borderRadius: 4, 
                            background: item.status === 'Verified' ? 'rgba(108, 117, 125, 0.15)' : '#ff6b35', 
                            color: item.status === 'Verified' ? '#6c757d' : 'white', 
                            border: item.status === 'Verified' ? '1px solid rgba(108, 117, 125, 0.3)' : 'none', 
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13,
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => window.open(`/request-fe-hours-by-external-activity?command=student&id=${item.id}`, '_blank')}
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  );
                } else if (item._source === 'coursework') {
                  // Coursework aggregated by course, appears in tabs per hour type
                  const bg = item.totalHours > 0 ? '#f5faff' : '#fffbf0';
                  const bcol = item.totalHours > 0 ? '#cde4ff' : '#ffeaa7';
                  return (
                    <div key={`course-${item.id}-${selectedType}`} className="item-hover" style={{ border: `2px solid ${bcol}`, borderRadius: 6, padding: '1rem', background: bg, position: 'relative' }}>
                      <div className="item-badge badge-project">Coursework</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2c3e50' }}>{item.course_title || 'Untitled Course'}</div>
                          <div style={{ color: '#6c757d', fontSize: '0.95rem', marginTop: 2 }}>
                            {item.totalHours > 0 ? (
                              <>
                                <span style={{ color: '#495057', fontWeight: 600 }}>{item.totalHours}</span> hours earned (
                                {(() => {
                                  const parts = [];
                                  const entries = Object.entries(item.hoursByType || {});
                                  entries.forEach(([type, val], idx) => {
                                    if (val > 0) {
                                      const isCurrentTab = type === selectedType;
                                      parts.push(
                                        <span key={`${item.id}-${type}`} style={isCurrentTab ? { color: '#0f5132', fontWeight: 700 } : {}}>
                                          {val} {type}
                                        </span>
                                      );
                                    }
                                  });
                                  // Insert commas between parts
                                  return parts.reduce((acc, el, i) => {
                                    if (i === 0) return [el];
                                    return [...acc, ', ', el];
                                  }, []);
                                })()}
                                )
                              </>
                            ) : 'No hours earned yet'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            className={item.totalHours > 0 ? 'subtle-button' : ''}
                            style={{ 
                              padding: '6px 16px', 
                              borderRadius: 4, 
                              background: item.totalHours > 0 ? 'rgba(108, 117, 125, 0.15)' : '#1976d2', 
                              color: item.totalHours > 0 ? '#6c757d' : 'white', 
                              border: item.totalHours > 0 ? '1px solid rgba(108, 117, 125, 0.3)' : 'none', 
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13,
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => window.open(`/course-viewer?command=coursework&id=${item.course}`, '_blank')}
                          >
                            Open Course
                          </button>
                        </div>
                      </div>

                      {/* Expandable submissions list within course card */}
                      {item.submissions && item.submissions.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <details>
                            <summary style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600, fontSize: 13 }}>View submissions</summary>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                              {[...item.submissions]
                                .sort((a, b) => new Date(b.createddate) - new Date(a.createddate))
                                .map(sub => (
                                <div key={sub.id} style={{
                                  padding: '10px',
                                  backgroundColor: sub.status === 'completed' ? '#e8f5e8' : '#fff3cd',
                                  border: `1px solid ${sub.status === 'completed' ? '#b6e7c9' : '#ffeaa7'}`,
                                  borderRadius: 6,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 8
                                }}>
                                  <div>
                                    <div style={{ fontWeight: 500, fontSize: 14, color: '#495057' }}>{sub.component_title || 'Untitled Component'}</div>
                                    <div style={{ fontSize: 12, color: '#6c757d' }}>{sub.component_type} • {new Date(sub.createddate).toLocaleString()} • {sub.status}</div>
                                  </div>
                                  <div>
                                    {sub.hours_earned && (
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: 4,
                                        fontSize: 11,
                                        fontWeight: 500,
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {(() => {
                                          try {
                                            const hours = typeof sub.hours_earned === 'string' ? JSON.parse(sub.hours_earned) : sub.hours_earned;
                                            if (Array.isArray(hours)) {
                                              return hours.map(h => `${h.hours} ${h.type}`).join(', ');
                                            }
                                          } catch {}
                                          return 'Hours earned';
                                        })()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
                </div>
              </>
            )}
          </div>

          
          
          {/* Action Popup */}
          {showActionPopup && (
            <div className="popup-overlay" onClick={() => setShowActionPopup(false)}>
              <div className="popup" onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                  <h3>Earn More Hours</h3>
                  <button className="popup-close" onClick={() => setShowActionPopup(false)}>×</button>
                </div>
                <div className="popup-content">
                  <div className="popup-option" onClick={() => {
                    window.open('/register-for-field-experience', '_blank');
                    setShowActionPopup(false);
                  }}>
                    <div className="option-icon">📅</div>
                    <div className="option-content">
                      <div className="option-title">Register for an Event</div>
                      <div className="option-subtext">Attend field experience events, workshops, and hands-on learning opportunities</div>
                    </div>
                  </div>
                  
                  <div className="popup-option" onClick={() => {
                    window.open('/my-courses', '_blank');
                    setShowActionPopup(false);
                  }}>
                    <div className="option-icon">📘</div>
                    <div className="option-content">
                      <div className="option-title">View Coursework</div>
                      <div className="option-subtext">Access your assigned courses and complete coursework in a structured way</div>
                    </div>
                  </div>
                  
                  <div className="popup-option" onClick={() => {
                    window.open('/projects-directory', '_blank');
                    setShowActionPopup(false);
                  }}>
                    <div className="option-icon">🔍</div>
                    <div className="option-content">
                      <div className="option-title">Search for Projects</div>
                      <div className="option-subtext">Complete special assignments and projects to earn additional hours</div>
                    </div>
                  </div>
                  
                  <div className="popup-option" onClick={() => {
                    window.open('/request-fe-hours-by-external-activity', '_blank');
                    setShowActionPopup(false);
                  }}>
                    <div className="option-icon">📝</div>
                    <div className="option-content">
                      <div className="option-title">Start a Waiver Form</div>
                      <div className="option-subtext">Get credit for external activities like internships, volunteer work, or professional development</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyHours;